import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import {
  CalendarDays,
  Bell,
  Bookmark,
  Check,
  ChevronLeft,
  ChevronRight,
  CirclePlus,
  ClipboardCheck,
  Clock3,
  House,
  Download,
  ListChecks,
  Plus,
  Quote,
  Search,
  Settings,
  Share2,
  SlidersHorizontal,
  Upload,
  X,
} from 'lucide-react'
import { CATEGORY_META, createSeedItems } from './data'
import {
  formatFullDate,
  formatMonthTitle,
  fromDateKey,
  getCalendarDays,
  getLunarDate,
  getLunarLabel,
  isSameDay,
  shiftMonth,
  toDateKey,
} from './lib/date'
import { isCompletedOn, occursOnDate } from './lib/recurrence'
import { formatDday, getUpcomingAnniversaries } from './lib/dday'
import type { UpcomingAnniversary } from './lib/dday'
import {
  disableNotifications,
  enableNotifications,
  hideCalendarBanner,
  isNativeApp,
  shareDailyQuote,
  shareLifeItem,
  showAdPrivacyOptions,
  showCalendarBanner,
  syncNotifications,
  syncWidgetSnapshot,
} from './lib/native'
import { loadState, normalizeState, saveState } from './lib/storage'
import type { CategoryId, DailyContentMode, DateMarkType, ItemKind, LifeItem, RepeatRule, TabId } from './models'
import { getDailyBibleVerse, getDailyContentsByIds, getDailyQuote } from './quotes'
import type { DailyQuote } from './quotes'

const NAV_ITEMS: Array<{ id: TabId; label: string; icon: typeof House }> = [
  { id: 'today', label: '오늘', icon: House },
  { id: 'calendar', label: '달력', icon: CalendarDays },
  { id: 'records', label: '기록', icon: ClipboardCheck },
  { id: 'settings', label: '설정', icon: Settings },
]

const REPEAT_LABEL: Record<RepeatRule, string> = {
  none: '반복 안 함',
  daily: '매일',
  weekly: '매주',
  monthly: '매월',
  'yearly-solar': '매년 양력',
  'yearly-lunar': '매년 음력',
}

function makeId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
}

function App() {
  const [today, setToday] = useState(() => new Date())
  const [appState, setAppState] = useState(() => loadState(createSeedItems(today)))
  const dailyQuote = useMemo(
    () => appState.settings.dailyContentMode === 'bible' ? getDailyBibleVerse(today) : getDailyQuote(today),
    [appState.settings.dailyContentMode, today],
  )
  const [activeTab, setActiveTab] = useState<TabId>('today')
  const [selectedDate, setSelectedDate] = useState(today)
  const [viewMonth, setViewMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const [isAddOpen, setIsAddOpen] = useState(false)
  const shouldShowBanner = isNativeApp()
    && !isAddOpen
    && activeTab !== 'settings'
  const upcomingAnniversaries = useMemo(
    () => getUpcomingAnniversaries(appState.items, today),
    [appState.items, today],
  )

  useEffect(() => {
    const refreshDate = () => {
      const next = new Date()
      setToday((current) => toDateKey(current) === toDateKey(next) ? current : next)
    }
    const intervalId = window.setInterval(refreshDate, 60_000)
    document.addEventListener('visibilitychange', refreshDate)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', refreshDate)
    }
  }, [])

  useEffect(() => {
    if (appState.settings.notificationsEnabled) void syncNotifications(appState.items)
  }, [appState.items, appState.settings.notificationsEnabled])

  useEffect(() => {
    void syncWidgetSnapshot(
      appState.items,
      appState.settings.showLunar,
      appState.settings.dailyContentMode !== 'off' ? dailyQuote.text : '',
    )
  }, [appState.items, appState.settings.dailyContentMode, appState.settings.showLunar, dailyQuote.text])

  useEffect(() => {
    const updateBanner = () => {
      if (shouldShowBanner && document.visibilityState !== 'hidden') void showCalendarBanner()
      else void hideCalendarBanner()
    }
    updateBanner()
    document.addEventListener('visibilitychange', updateBanner)
    window.addEventListener('online', updateBanner)
    return () => {
      document.removeEventListener('visibilitychange', updateBanner)
      window.removeEventListener('online', updateBanner)
      void hideCalendarBanner()
    }
  }, [shouldShowBanner])

  const navigateTo = (tab: TabId) => {
    setActiveTab(tab)
    requestAnimationFrame(() => document.querySelector('.main-content')?.scrollTo({ top: 0 }))
  }

  const updateState = (updater: (current: typeof appState) => typeof appState) => {
    setAppState((current) => {
      const next = updater(current)
      saveState(next)
      return next
    })
  }

  const toggleComplete = (id: string, dateKey: string) => {
    updateState((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === id
          ? {
              ...item,
              completedDates: item.completedDates.includes(dateKey)
                ? item.completedDates.filter((key) => key !== dateKey)
                : [...item.completedDates, dateKey],
            }
          : item,
      ),
    }))
  }

  const addItem = (item: LifeItem) => {
    updateState((current) => ({ ...current, items: [...current.items, item] }))
    setSelectedDate(fromDateKey(item.date))
    setIsAddOpen(false)
  }

  const toggleFavoriteQuote = (quoteId: string) => {
    updateState((current) => ({
      ...current,
      favoriteQuoteIds: current.favoriteQuoteIds.includes(quoteId)
        ? current.favoriteQuoteIds.filter((id) => id !== quoteId)
        : [...current.favoriteQuoteIds, quoteId],
    }))
  }

  const toggleDateMark = (date: string, type: DateMarkType) => {
    updateState((current) => {
      const existing = current.dateMarks.find((mark) => mark.date === date)
      return {
        ...current,
        dateMarks: existing?.type === type
          ? current.dateMarks.filter((mark) => mark.date !== date)
          : [...current.dateMarks.filter((mark) => mark.date !== date), { date, type }],
      }
    })
  }

  const changeNotifications = async (enabled: boolean) => {
    if (!enabled) {
      await disableNotifications()
      updateState((current) => ({
        ...current,
        settings: { ...current.settings, notificationsEnabled: false },
      }))
      return
    }
    if (!isNativeApp()) {
      window.alert('일정 알림은 Android 앱에서 사용할 수 있습니다.')
      return
    }
    const granted = await enableNotifications(appState.items)
    if (!granted) {
      window.alert('알림 권한이 허용되지 않았습니다.')
      return
    }
    updateState((current) => ({
      ...current,
      settings: { ...current.settings, notificationsEnabled: true },
    }))
  }

  const exportBackup = () => {
    const blob = new Blob([JSON.stringify(appState, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `큰글달력-백업-${toDateKey(new Date())}.json`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const importBackup = async (file: File) => {
    try {
      const value = normalizeState(JSON.parse(await file.text()))
      updateState(() => value)
      window.alert('백업을 불러왔습니다.')
    } catch {
      window.alert('올바른 생활달력 백업 파일이 아닙니다.')
    }
  }

  const selectedKey = toDateKey(selectedDate)
  const selectedItems = appState.items
    .filter((item) => occursOnDate(item, selectedDate))
    .sort((left, right) => (left.time ?? '99:99').localeCompare(right.time ?? '99:99'))

  return (
    <div className="app" style={{ '--font-scale': appState.settings.fontScale } as React.CSSProperties}>
      <header className="topbar">
        <button className="brand" onClick={() => navigateTo('today')} aria-label="오늘 화면으로 이동">
          <span className="brand-mark" aria-hidden="true"><CalendarDays size={24} /></span>
          <span>
            <strong>큰글달력</strong>
            <small>오늘 날짜와 할 일을 크게</small>
          </span>
        </button>
        <button className="today-chip" onClick={() => {
          setSelectedDate(today)
          setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1))
          navigateTo('today')
        }}>
          오늘
        </button>
      </header>

      <main className="main-content">
        {activeTab === 'today' && (
          <TodayView
            date={today}
            items={appState.items.filter((item) => occursOnDate(item, today))}
            showLunar={appState.settings.showLunar}
            dailyContentMode={appState.settings.dailyContentMode}
            dailyQuote={dailyQuote}
            upcomingAnniversaries={upcomingAnniversaries}
            quoteSaved={appState.favoriteQuoteIds.includes(dailyQuote.id)}
            onToggleQuote={() => toggleFavoriteQuote(dailyQuote.id)}
            onShareQuote={() => void shareDailyQuote(dailyQuote)}
            onToggle={toggleComplete}
            onShare={(item, dateKey) => void shareLifeItem(item, dateKey)}
            onAdd={() => {
              setSelectedDate(today)
              setIsAddOpen(true)
            }}
          />
        )}

        {activeTab === 'calendar' && (
          <CalendarView
            today={today}
            selectedDate={selectedDate}
            viewMonth={viewMonth}
            items={appState.items}
            showLunar={appState.settings.showLunar}
            selectedItems={selectedItems}
            dateMarks={appState.dateMarks}
            onMonthChange={(amount) => setViewMonth((current) => shiftMonth(current, amount))}
            onSelectDate={setSelectedDate}
            onJumpToDate={(date) => {
              setSelectedDate(date)
              setViewMonth(new Date(date.getFullYear(), date.getMonth(), 1))
            }}
            onToggleDateMark={toggleDateMark}
            onToggle={toggleComplete}
            onShare={(item, dateKey) => void shareLifeItem(item, dateKey)}
            onAdd={() => setIsAddOpen(true)}
          />
        )}

        {activeTab === 'records' && (
          <RecordsView
            items={appState.items}
            favoriteQuotes={getDailyContentsByIds(appState.favoriteQuoteIds)}
            onToggleQuote={toggleFavoriteQuote}
            onShareQuote={(quote) => void shareDailyQuote(quote)}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsView
            fontScale={appState.settings.fontScale}
            showLunar={appState.settings.showLunar}
            dailyContentMode={appState.settings.dailyContentMode}
            onFontScale={(fontScale) => updateState((current) => ({
              ...current,
              settings: { ...current.settings, fontScale },
            }))}
            onShowLunar={(showLunar) => updateState((current) => ({
              ...current,
              settings: { ...current.settings, showLunar },
            }))}
            onDailyContentMode={(dailyContentMode) => updateState((current) => ({
              ...current,
              settings: { ...current.settings, dailyContentMode },
            }))}
            onExport={exportBackup}
            onImport={importBackup}
            notificationsEnabled={appState.settings.notificationsEnabled}
            onNotifications={(enabled) => void changeNotifications(enabled)}
            onAdPrivacy={() => void showAdPrivacyOptions()}
          />
        )}
      </main>

      <nav className="bottom-nav" aria-label="주 메뉴">
        {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={activeTab === id ? 'active' : ''}
            onClick={() => navigateTo(id)}
            aria-current={activeTab === id ? 'page' : undefined}
          >
            <Icon size={24} strokeWidth={2.2} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {isAddOpen && (
        <AddItemDialog
          initialDate={selectedKey}
          onClose={() => setIsAddOpen(false)}
          onSubmit={addItem}
        />
      )}
    </div>
  )
}

interface TodayViewProps {
  date: Date
  items: LifeItem[]
  showLunar: boolean
  dailyContentMode: DailyContentMode
  dailyQuote: DailyQuote
  upcomingAnniversaries: UpcomingAnniversary[]
  quoteSaved: boolean
  onToggleQuote: () => void
  onShareQuote: () => void
  onToggle: (id: string, dateKey: string) => void
  onShare: (item: LifeItem, dateKey: string) => void
  onAdd: () => void
}

function TodayView({ date, items, showLunar, dailyContentMode, dailyQuote, upcomingAnniversaries, quoteSaved, onToggleQuote, onShareQuote, onToggle, onShare, onAdd }: TodayViewProps) {
  const [showCompleted, setShowCompleted] = useState(false)
  const dateKey = toDateKey(date)
  const sortByTime = (left: LifeItem, right: LifeItem) => (left.time ?? '99:99').localeCompare(right.time ?? '99:99')
  const pending = items.filter((item) => !isCompletedOn(item, dateKey)).sort(sortByTime)
  const complete = items.filter((item) => isCompletedOn(item, dateKey)).sort(sortByTime)

  return (
    <section className="screen today-screen">
      <div className="date-hero">
        <p className="eyebrow">오늘</p>
        <h1>{formatFullDate(date)}</h1>
        {showLunar && <p className="lunar-label">{getLunarLabel(date)}</p>}
      </div>

      <div className="summary-card">
        <div>
          <strong>{pending.length}</strong>
          <span>남은 일</span>
        </div>
        <div className="summary-divider" />
        <div>
          <strong>{complete.length}</strong>
          <span>완료</span>
        </div>
        <div className="summary-message">
          {pending.length === 0 ? '오늘 할 일을 모두 마쳤어요.' : '하나씩 천천히 해볼까요?'}
        </div>
      </div>

      <div className="section-heading">
        <div>
          <p className="eyebrow">오늘의 목록</p>
          <h2>해야 할 일</h2>
        </div>
        <span className="count-badge">{items.length}개</span>
      </div>

      <div className="item-list">
        {items.length === 0 ? (
          <EmptyState onAdd={onAdd} />
        ) : (
          <>
            {pending.map((item) => <ItemCard key={item.id} item={item} dateKey={dateKey} onToggle={onToggle} onShare={onShare} />)}
            {complete.length > 0 && (
              <button
                className="completed-toggle"
                onClick={() => setShowCompleted((current) => !current)}
                aria-expanded={showCompleted}
              >
                완료한 일 {complete.length}개 {showCompleted ? '숨기기' : '보기'}
                <ChevronRight className={showCompleted ? 'open' : ''} size={22} aria-hidden="true" />
              </button>
            )}
            {showCompleted && complete.map((item) => <ItemCard key={item.id} item={item} dateKey={dateKey} onToggle={onToggle} onShare={onShare} />)}
          </>
        )}
      </div>

      <button className="primary-action" onClick={onAdd}>
        <CirclePlus size={26} />
        새 일정 추가
      </button>

      {upcomingAnniversaries.length > 0 && (
        <section className="dday-panel" aria-labelledby="upcoming-anniversary-title">
          <div className="section-heading no-margin">
            <div><p className="eyebrow">다가오는 날</p><h2 id="upcoming-anniversary-title">기념일 D-day</h2></div>
          </div>
          <ul>
            {upcomingAnniversaries.map(({ item, date: anniversaryDate, daysLeft }) => (
              <li key={item.id}>
                <span aria-hidden="true">🎉</span>
                <div><strong>{item.title}</strong><small>{anniversaryDate.getMonth() + 1}월 {anniversaryDate.getDate()}일</small></div>
                <b>{formatDday(daysLeft)}</b>
              </li>
            ))}
          </ul>
        </section>
      )}

      {dailyContentMode !== 'off' && (
        <article className={`daily-quote-card${dailyQuote.kind === 'bible' ? ' bible' : ''}`}>
          <div className="quote-heading">
            <div>
              <p className="eyebrow">{dailyQuote.kind === 'bible' ? '오늘의 말씀' : '오늘의 한마디'}</p>
              <span>{dailyQuote.source}</span>
            </div>
            <Quote aria-hidden="true" />
          </div>
          <blockquote>“{dailyQuote.text}”</blockquote>
          <div className="quote-actions">
            <button className={quoteSaved ? 'saved' : ''} onClick={onToggleQuote} aria-pressed={quoteSaved}>
              <Bookmark size={20} fill={quoteSaved ? 'currentColor' : 'none'} />
              {quoteSaved ? '마음에 담았어요' : '마음에 담기'}
            </button>
            <button onClick={onShareQuote}>
              <Share2 size={20} /> 공유
            </button>
          </div>
        </article>
      )}
    </section>
  )
}

interface CalendarViewProps {
  today: Date
  selectedDate: Date
  viewMonth: Date
  items: LifeItem[]
  showLunar: boolean
  selectedItems: LifeItem[]
  dateMarks: Array<{ date: string; type: DateMarkType }>
  onMonthChange: (amount: number) => void
  onSelectDate: (date: Date) => void
  onJumpToDate: (date: Date) => void
  onToggleDateMark: (date: string, type: DateMarkType) => void
  onToggle: (id: string, dateKey: string) => void
  onShare: (item: LifeItem, dateKey: string) => void
  onAdd: () => void
}

function CalendarView(props: CalendarViewProps) {
  const days = getCalendarDays(props.viewMonth)
  const [searchQuery, setSearchQuery] = useState('')
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase('ko')
  const searchResults = normalizedQuery
    ? props.items.filter((item) => {
      const category = CATEGORY_META[item.category].label
      return `${item.title} ${item.note ?? ''} ${category}`.toLocaleLowerCase('ko').includes(normalizedQuery)
    }).slice(0, 30)
    : []
  const selectedKey = toDateKey(props.selectedDate)
  const selectedMark = props.dateMarks.find((mark) => mark.date === selectedKey)?.type

  return (
    <section className="screen calendar-screen">
      <div className="month-toolbar">
        <button className="icon-button" onClick={() => props.onMonthChange(-1)} aria-label="이전 달">
          <ChevronLeft />
        </button>
        <h1>{formatMonthTitle(props.viewMonth)}</h1>
        <button className="icon-button" onClick={() => props.onMonthChange(1)} aria-label="다음 달">
          <ChevronRight />
        </button>
      </div>

      <label className="calendar-search">
        <Search size={22} aria-hidden="true" />
        <span className="sr-only">일정 검색</span>
        <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="일정이나 메모 검색" />
        {searchQuery && <button type="button" onClick={() => setSearchQuery('')} aria-label="검색어 지우기"><X size={19} /></button>}
      </label>

      {normalizedQuery ? (
        <section className="search-results" aria-live="polite">
          <div className="section-heading no-margin">
            <div><p className="eyebrow">검색 결과</p><h2>{searchResults.length}개 찾았어요</h2></div>
          </div>
          {searchResults.length === 0 ? <p className="inline-empty">찾는 일정이 없습니다.</p> : (
            <ul>
              {searchResults.map((item) => (
                <li key={item.id}>
                  <button onClick={() => {
                    const date = fromDateKey(item.date)
                    props.onJumpToDate(date)
                    setSearchQuery('')
                  }}>
                    <span>{CATEGORY_META[item.category].icon}</span>
                    <div><strong>{item.title}</strong><small>{item.date}{item.note ? ` · ${item.note}` : ''}</small></div>
                    <ChevronRight aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <>

      <div className="calendar-card">
        <div className="weekday-row" aria-hidden="true">
          {['일', '월', '화', '수', '목', '금', '토'].map((day) => <span key={day}>{day}</span>)}
        </div>
        <div className="calendar-grid">
          {days.map((date) => {
            const key = toDateKey(date)
            const dayItems = props.items.filter((item) => occursOnDate(item, date))
            const outside = date.getMonth() !== props.viewMonth.getMonth()
            const selected = isSameDay(date, props.selectedDate)
            const today = isSameDay(date, props.today)
            const dateMark = props.dateMarks.find((mark) => mark.date === key)
            return (
              <button
                key={key}
                className={`calendar-day${outside ? ' outside' : ''}${selected ? ' selected' : ''}${today ? ' today' : ''}`}
                onClick={() => props.onSelectDate(date)}
                aria-label={`${date.getMonth() + 1}월 ${date.getDate()}일, 일정 ${dayItems.length}개`}
              >
                <span className="day-number">{date.getDate()}</span>
                {dateMark && <span className={`date-mark ${dateMark.type}`} aria-hidden="true">{dateMark.type === 'circle' ? '○' : dateMark.type === 'cross' ? '×' : '★'}</span>}
                {props.showLunar && !outside && <small>{getLunarLabel(date).replace('음력 ', '').replace('월 ', '.').replace('일', '')}</small>}
                <span className="event-dots">
                  {dayItems.slice(0, 3).map((item) => (
                    <i key={item.id} style={{ backgroundColor: CATEGORY_META[item.category].color }} />
                  ))}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="selected-day-header">
        <div>
          <p className="eyebrow">선택한 날짜</p>
          <h2>{formatFullDate(props.selectedDate)}</h2>
          {props.showLunar && <p>{getLunarLabel(props.selectedDate)}</p>}
        </div>
        <button className="small-add-button" onClick={props.onAdd}><Plus size={22} /> 추가</button>
      </div>

      <div className="date-mark-picker" role="group" aria-label={`${formatFullDate(props.selectedDate)} 날짜 표시`}>
        <span>날짜 표시</span>
        {([['circle', '○', '동그라미'], ['cross', '×', '엑스'], ['star', '★', '별']] as Array<[DateMarkType, string, string]>).map(([type, symbol, label]) => (
          <button
            key={type}
            className={selectedMark === type ? 'active' : ''}
            onClick={() => props.onToggleDateMark(selectedKey, type)}
            aria-pressed={selectedMark === type}
          >
            <b>{symbol}</b>{label}
          </button>
        ))}
      </div>

      <div className="item-list compact-list">
        {props.selectedItems.length === 0
          ? <p className="inline-empty">등록된 일정이 없습니다.</p>
          : props.selectedItems.map((item) => <ItemCard key={item.id} item={item} dateKey={toDateKey(props.selectedDate)} onToggle={props.onToggle} onShare={props.onShare} />)}
      </div>
        </>
      )}
    </section>
  )
}

function ItemCard({ item, dateKey, onToggle, onShare }: { item: LifeItem; dateKey: string; onToggle: (id: string, dateKey: string) => void; onShare: (item: LifeItem, dateKey: string) => void }) {
  const category = CATEGORY_META[item.category]
  const completed = isCompletedOn(item, dateKey)
  return (
    <article className={`item-card${completed ? ' completed' : ''}`}>
      <div className="category-icon" style={{ backgroundColor: `${category.color}18`, color: category.color }} aria-hidden="true">
        {category.icon}
      </div>
      <div className="item-copy">
        <div className="item-meta">
          <span>{category.label}</span>
          {item.time && <span><Clock3 size={15} /> {item.time}</span>}
        </div>
        <h3>{item.title}</h3>
        {item.repeat !== 'none' && <p>{REPEAT_LABEL[item.repeat]} 반복</p>}
      </div>
      <div className="item-actions">
        <button className="share-button" onClick={() => onShare(item, dateKey)} aria-label={`${item.title} 공유`}><Share2 size={19} /></button>
        <button
          className={`complete-button${completed ? ' done' : ''}`}
          onClick={() => onToggle(item.id, dateKey)}
          aria-label={`${item.title} ${completed ? '미완료로 변경' : '완료'}`}
        >
          {completed ? <Check size={23} /> : <span>완료</span>}
        </button>
      </div>
    </article>
  )
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="empty-state">
      <span aria-hidden="true">🌿</span>
      <h3>오늘은 여유로운 날이에요</h3>
      <p>기억할 일정이나 할 일이 있다면 추가해 보세요.</p>
      <button onClick={onAdd}>첫 일정 추가하기</button>
    </div>
  )
}

function RecordsView({ items, favoriteQuotes, onToggleQuote, onShareQuote }: {
  items: LifeItem[]
  favoriteQuotes: DailyQuote[]
  onToggleQuote: (quoteId: string) => void
  onShareQuote: (quote: DailyQuote) => void
}) {
  const completed = items.reduce((total, item) => total + item.completedDates.length, 0)
  const completedItems = items.filter((item) => item.completedDates.length > 0).length
  const routines = items.filter((item) => item.kind === 'routine')
  const completionRate = items.length === 0 ? 0 : Math.round((completedItems / items.length) * 100)

  return (
    <section className="screen records-screen">
      <div className="page-heading">
        <p className="eyebrow">나의 생활</p>
        <h1>기록</h1>
        <p>완료한 일과 생활 루틴을 편안하게 돌아보세요.</p>
      </div>
      <div className="record-grid">
        <article className="record-card coral"><ListChecks /><strong>{completed}개</strong><span>완료한 일</span></article>
        <article className="record-card yellow"><ClipboardCheck /><strong>{completionRate}%</strong><span>전체 완료율</span></article>
      </div>
      <article className="progress-panel">
        <div className="section-heading no-margin">
          <div><p className="eyebrow">생활 루틴</p><h2>최근 등록한 루틴</h2></div>
        </div>
        {routines.length === 0 ? <p className="inline-empty">아직 등록한 루틴이 없습니다.</p> : (
          <ul className="simple-list">
            {routines.map((item) => (
              <li key={item.id}><span>{item.completedDates.length > 0 ? '✓' : '–'}</span><strong>{item.title}</strong><small>{REPEAT_LABEL[item.repeat]}</small></li>
            ))}
          </ul>
        )}
      </article>
      <article className="saved-quotes-panel">
        <div className="section-heading no-margin">
          <div><p className="eyebrow">마음에 담기</p><h2>저장한 문구와 말씀</h2></div>
          <span className="count-badge">{favoriteQuotes.length}개</span>
        </div>
        {favoriteQuotes.length === 0 ? (
          <p className="inline-empty">오늘 화면에서 마음에 드는 문구나 말씀을 담아 보세요.</p>
        ) : (
          <ul className="saved-quote-list">
            {favoriteQuotes.map((quote) => (
              <li key={quote.id}>
                <div><q>{quote.text}</q><small>{quote.source}</small></div>
                <div>
                  <button onClick={() => onShareQuote(quote)} aria-label={`${quote.text} 공유`}><Share2 size={19} /></button>
                  <button onClick={() => onToggleQuote(quote.id)} aria-label={`${quote.text} 저장 해제`}><X size={19} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </article>
    </section>
  )
}

interface SettingsViewProps {
  fontScale: number
  showLunar: boolean
  dailyContentMode: DailyContentMode
  onFontScale: (value: number) => void
  onShowLunar: (value: boolean) => void
  onDailyContentMode: (value: DailyContentMode) => void
  onExport: () => void
  onImport: (file: File) => void
  notificationsEnabled: boolean
  onNotifications: (value: boolean) => void
  onAdPrivacy: () => void
}

function SettingsView({ fontScale, showLunar, dailyContentMode, onFontScale, onShowLunar, onDailyContentMode, onExport, onImport, notificationsEnabled, onNotifications, onAdPrivacy }: SettingsViewProps) {
  const fontOptions = [
    { value: 1, label: '보통', sample: '가' },
    { value: 1.15, label: '크게', sample: '가' },
    { value: 1.3, label: '아주 크게', sample: '가' },
  ]
  return (
    <section className="screen settings-screen">
      <div className="page-heading">
        <p className="eyebrow">내게 편한 화면</p>
        <h1>설정</h1>
      </div>
      <div className="settings-group">
        <div className="setting-row font-setting">
          <div className="setting-icon"><SlidersHorizontal /></div>
          <div className="setting-copy"><strong>글씨 크기</strong><span>읽기 편한 크기를 고르세요.</span></div>
          <div className="font-presets" role="radiogroup" aria-label="글씨 크기">
            {fontOptions.map((option, index) => (
              <button
                key={option.value}
                className={Math.abs(fontScale - option.value) < 0.01 ? 'selected' : ''}
                onClick={() => onFontScale(option.value)}
                role="radio"
                aria-checked={Math.abs(fontScale - option.value) < 0.01}
              >
                <span style={{ fontSize: `${1 + index * 0.18}em` }}>{option.sample}</span>
                <strong>{option.label}</strong>
              </button>
            ))}
          </div>
        </div>
        <label className="setting-row">
          <div className="setting-icon lunar-icon">음</div>
          <div className="setting-copy"><strong>음력 날짜 표시</strong><span>양력 날짜 아래 음력을 함께 표시합니다.</span></div>
          <input className="switch" type="checkbox" checked={showLunar} onChange={(event) => onShowLunar(event.target.checked)} />
        </label>
        <div className="setting-row content-setting">
          <div className="setting-icon"><Quote /></div>
          <div className="setting-copy"><strong>오늘의 문구</strong><span>원하는 내용을 날짜마다 새롭게 보여드립니다.</span></div>
          <div className="content-presets" role="radiogroup" aria-label="오늘의 문구 종류">
            {([
              ['quotes', '좋은 문구'],
              ['bible', '성경 말씀'],
              ['off', '표시 안 함'],
            ] as Array<[DailyContentMode, string]>).map(([value, label]) => (
              <button
                key={value}
                className={dailyContentMode === value ? 'selected' : ''}
                onClick={() => onDailyContentMode(value)}
                role="radio"
                aria-checked={dailyContentMode === value}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <label className="setting-row">
          <div className="setting-icon"><Bell /></div>
          <div className="setting-copy"><strong>일정 알림</strong><span>시간을 등록한 일정을 휴대전화에서 알려드립니다.</span></div>
          <input className="switch" type="checkbox" checked={notificationsEnabled} onChange={(event) => onNotifications(event.target.checked)} />
        </label>
      </div>
      <div className="backup-panel">
        <div>
          <strong>일정 백업</strong>
          <p>파일로 보관했다가 같은 기기나 새 기기에서 다시 불러올 수 있습니다.</p>
        </div>
        <div className="backup-actions">
          <button onClick={onExport}><Download size={21} /> 백업 저장</button>
          <label><Upload size={21} /> 백업 불러오기<input type="file" accept="application/json,.json" onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) onImport(file)
            event.target.value = ''
          }} /></label>
        </div>
      </div>
      <div className="settings-links">
        <button onClick={onAdPrivacy}>광고 개인정보 선택</button>
        <a href="/privacy.html">개인정보처리방침</a>
        <a href="/support.html">고객지원</a>
      </div>
      <div className="privacy-note">
        <strong>내 일정은 내 휴대전화에</strong>
        <p>현재 버전은 일정과 생활 루틴을 기기 내부에만 저장합니다.</p>
      </div>
    </section>
  )
}

interface AddItemDialogProps {
  initialDate: string
  onClose: () => void
  onSubmit: (item: LifeItem) => void
}

function AddItemDialog({ initialDate, onClose, onSubmit }: AddItemDialogProps) {
  const [kind, setKind] = useState<ItemKind>('schedule')
  const [title, setTitle] = useState('')
  const [date, setDate] = useState(initialDate)
  const [time, setTime] = useState('')
  const [category, setCategory] = useState<CategoryId>('general')
  const [repeat, setRepeat] = useState<RepeatRule>('none')
  const [note, setNote] = useState('')

  const selectKind = (value: ItemKind) => {
    setKind(value)
    if (value === 'anniversary') {
      if (repeat === 'none') setRepeat('yearly-solar')
      setCategory('celebration')
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim()) return
    onSubmit({
      id: makeId(),
      title: title.trim(),
      date,
      time: time || undefined,
      kind,
      category: kind === 'routine' ? 'routine' : category,
      completedDates: [],
      repeat,
      note: note.trim() || undefined,
      lunarRepeat: repeat === 'yearly-lunar' ? getLunarDate(fromDateKey(date)) ?? undefined : undefined,
    })
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section className="add-dialog" role="dialog" aria-modal="true" aria-labelledby="add-dialog-title">
        <div className="dialog-handle" />
        <header className="dialog-header">
          <div><p className="eyebrow">새로 등록하기</p><h2 id="add-dialog-title">무엇을 기억할까요?</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="닫기"><X /></button>
        </header>
        <form onSubmit={submit}>
          <div className="kind-grid" role="group" aria-label="등록 종류">
            {([
              ['schedule', '📅', '일정'],
              ['task', '📌', '할 일'],
              ['routine', '✅', '생활 루틴'],
              ['anniversary', '🎉', '기념일'],
            ] as Array<[ItemKind, string, string]>).map(([value, icon, label]) => (
              <button key={value} type="button" className={kind === value ? 'selected' : ''} onClick={() => selectKind(value)}>
                <span>{icon}</span>{label}
              </button>
            ))}
          </div>

          <label className="field-label">제목
            <input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} placeholder="예: 가족 모임" maxLength={40} />
          </label>
          <div className="two-column-fields">
            <label className="field-label">날짜<input type="date" value={date} onChange={(event) => setDate(event.target.value)} required /></label>
            <label className="field-label">시간 <small>선택</small><input type="time" value={time} onChange={(event) => setTime(event.target.value)} /></label>
          </div>
          {kind !== 'routine' && (
            <label className="field-label">분류
              <select value={category} onChange={(event) => setCategory(event.target.value as CategoryId)}>
                {Object.entries(CATEGORY_META).filter(([id]) => id !== 'routine').map(([id, meta]) => (
                  <option key={id} value={id}>{meta.icon} {meta.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="field-label">반복
            <select value={repeat} onChange={(event) => setRepeat(event.target.value as RepeatRule)}>
              {Object.entries(REPEAT_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="field-label">메모 <small>선택</small>
            <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="예: 선물 준비하기" maxLength={80} />
          </label>
          <button className="submit-button" type="submit" disabled={!title.trim()}><Check /> 저장하기</button>
        </form>
      </section>
    </div>
  )
}

export default App
